import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../../apiRequest";
import { endPoints } from "../../../apis";
import { useNavigate } from "react-router-dom"; 

function FormListing() {
  const navigate = useNavigate();
  const [customForms, setCustomForms] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchCustomForms = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems };
        const headers = {
          "x-access-token": accessToken,
        };
        const response = await apiRequest.get(endPoints.GET_FORMS, params, headers);

        if (response.success) {
          const { items, pagination } = response.data;
          setCustomForms(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching Form:", error);
        setError("Failed to fetch Form. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [accessToken, totalItems]
  );

  useEffect(() => {
    setAccessToken(localStorage.getItem("access_token"));
    fetchCustomForms(currentPage);
  }, [currentPage, fetchCustomForms]);


  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await apiRequest.delete(
        `${endPoints.DELETE_FORM}/${id}`,
        {
          headers: {
            'x-access-token': accessToken,
          },
        }
      );

      if (response.success) {
        setSuccess("Form deleted successfully.");
        setCustomForms((prev) => prev.filter((newData) => newData.id !== id));
      } else {
        setError(response.message || "Failed to delete the Form.");
      }
    } catch (error) {
      console.error("Error deleting Form:", error);
      setError("An error occurred while deleting the Form.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleCreateForm = () => {
    navigate("/dashboard/settings/create-form"); // Navigate to CreateForm page for creating
  };

  const handleEdit = (form) => {
    navigate(`/dashboard/settings/update-form/${form.id}`); // Navigate to CreateForm with form id for editing
  };

  const handleStatusToggle = async (id, currentStatus) => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
  
        const newStatus = currentStatus === "active" ? "inactive" : "active";
        const payload = {
          status: newStatus,
        };
  
        const headers = {
          'x-access-token': accessToken,
        };
  
        // Send the PUT request to update the slider status
        const response = await apiRequest.put(
          `${endPoints.STATUS_FORM}/${id}`,
          payload,
          { headers }
        );
  
        // Handle the response
        if (response.success) {
          setSuccess("form status updated successfully.");
  
          // Update the local state immediately
          setCustomForms((prev) =>
            prev.map((newData) =>
              newData.id === id ? { ...newData, status: newStatus } : newData
            )
          );
        } else {
          setError(response.message || 'Failed to update form status.');
        }
      } catch (error) {
        console.error('Error updating form status:', error);
        setError('An error occurred while updating the form status.');
      } finally {
        setLoading(false);
      }
    };


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Custom Forms
                </MDTypography>
              </MDBox>
              <MDBox pt={1} px={1}>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={2}
                >
                  <TextField
                    label="Search"
                    variant="outlined"
                    margin="normal"
                    placeholder="Search by title or description"
                    style={{ width: "300px" }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateForm}
                    style={{
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    Add Custom Form
                  </Button>
                </MDBox>
                <TableContainer component={Paper} style={{ marginTop: "1px" }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell style={{ fontWeight: "bold" }}>Name</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
                      </TableRow>
                      {customForms.map((customForm) => (
                        <TableRow key={customForm.id}>
                          <TableCell>{customForm.name}</TableCell>
                          <TableCell>
                            <Switch
                              checked={customForm.status === "active"}
                              onChange={() => handleStatusToggle(customForm.id, customForm.status)}
                              color="primary"
                            />
                          </TableCell>
                           <TableCell>
                              <Button
                                onClick={() => handleEdit(customForm)}
                                color="primary"
                                size="small"
                              >
                                Edit
                              </Button>
                              <Button
                                onClick={() => {
                                  setCurrentId(customForm.id); // Set the service type ID to delete
                                  setDeleteDialogOpen(true); // Open the confirmation dialog
                                }}
                                color="error"
                                size="small"
                                style={{ marginLeft: "8px" }}
                              >
                                Delete
                              </Button>
                            </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{ color: 'white' }}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{ color: 'white' }}
                  >
                    Next
                  </Button>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Custom Form</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this form? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              handleDelete(currentId);
            }}
            color="error"
            size="small"
            style={{ marginLeft: "8px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default FormListing;